import {
  JobStatus,
  StoreRefSchema,
  JobTaskStatus,
  JobTaskListSchema,
  JobTaskDetailSchema,
  OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import type { ImageGallerySchema } from "@labeling-tools/api/lebron/generated/types.gen";
// import { PlanogramProductSchema } from "@labeling-tools/api/atlas";

// --- Kroger image overrides (disabled) ---
// To re-enable: uncomment the functions below and the PlanogramProductSchema
// import above. Then uncomment the Kroger override code in:
//   - PlanogramSection.tsx (import, isKroger/krogerOverrides, and the two props on <PlanogramViewer>)
//   - PlanogramOnlyWorkflow.tsx (import, isKroger/krogerOverrides, and the two props on <PlanogramViewer>)
//   - PlanogramModal.tsx (import, useAtlasPlanogram call, isKroger/krogerOverrides, and the two props on <PlanogramViewer>)
//
// export const getKrogerImageUrl = (
//   upc: string,
//   size: "thumbnail" | "medium",
// ): string => {
//   const digits = upc.replace(/\D/g, "");
//   const upc13 = digits.padStart(13, "0");
//   return `https://www.kroger.com/product/images/${size}/front/${upc13}`;
// };
//
// export const buildKrogerImageOverrides = (
//   products: PlanogramProductSchema[],
// ): { thumbnails: Record<string, string>; tooltips: Record<string, string> } => {
//   const thumbnails: Record<string, string> = {};
//   const tooltips: Record<string, string> = {};
//   for (const product of products) {
//     const upc = product.identifier ?? (product as any).upc;
//     if (!upc || !product.id) continue;
//     thumbnails[product.id] = getKrogerImageUrl(upc, "thumbnail");
//     tooltips[product.id] = getKrogerImageUrl(upc, "medium");
//   }
//   return { thumbnails, tooltips };
// };

export interface Job {
  id: string;
  store: StoreRefSchema;
  status: JobStatus;
  created_at: string;
}
export const getStatusColor = (status: Job["status"]) => {
  switch (status) {
    case JobStatus.NEEDS_REVIEW:
      return "orange";
    case JobStatus.IN_PROGRESS:
      return "blue";
    case JobStatus.COMPLETED:
      return "green";
    default:
      return "gray";
  }
};

export const getStatusText = (status: Job["status"]) => {
  switch (status) {
    case JobStatus.NEEDS_REVIEW:
      return "Needs Review";
    case JobStatus.IN_PROGRESS:
      return "In Progress";
    case JobStatus.COMPLETED:
      return "Completed";
    default:
      return "Unknown";
  }
};

export const getTaskStatusColor = (status: JobTaskStatus) => {
  switch (status) {
    case JobTaskStatus.PENDING:
      return "orange";
    case JobTaskStatus.COMPLETED:
      return "green";
    case JobTaskStatus.SKIPPED:
      return "gray";
  }
};

export const getTaskStatusText = (status: JobTaskStatus) => {
  switch (status) {
    case JobTaskStatus.PENDING:
      return "Pending";
    case JobTaskStatus.COMPLETED:
      return "Completed";
    case JobTaskStatus.SKIPPED:
      return "Skipped";
  }
};

export const getCurrentTaskNumber = (
  jobTasks: JobTaskListSchema,
  taskId: string,
) => {
  if (!jobTasks || !taskId || !jobTasks.tasks.length) {
    return {
      current: 0,
      total: 0,
    };
  }
  return {
    current: jobTasks.tasks.findIndex((task) => task.id === taskId) + 1,
    total: jobTasks.tasks.length,
  };
};

/**
 * Given a parcel and an empty area index, find the product_id with the highest
 * similarity score in `parcel.query.empty_area_to_product[emptyAreaIndex]`.
 */
export const getBestMatchProductId = (
  parcel: OoSParcelSchema,
  emptyAreaIndex: number,
): string | null => {
  const scores = parcel.query?.empty_area_to_product?.[emptyAreaIndex];
  const productIds = parcel.query?.product_ids;
  if (!scores?.length || !productIds?.length) return null;

  let bestIdx = 0;
  for (let i = 1; i < scores.length; i++) {
    if (scores[i] > scores[bestIdx]) bestIdx = i;
  }

  return productIds[bestIdx] ?? null;
};

/**
 * Given a parcel and an empty area index, find the top N product_ids with the
 * highest similarity scores in `parcel.query.empty_area_to_product[emptyAreaIndex]`.
 */
export const getTopMatchProductIds = (
  parcel: OoSParcelSchema,
  emptyAreaIndex: number,
  topN = 5,
): string[] => {
  const scores = parcel.query?.empty_area_to_product?.[emptyAreaIndex];
  const productIds = parcel.query?.product_ids;
  if (!scores?.length || !productIds?.length) return [];

  const indexed = scores.map((score, idx) => ({ score, idx }));
  indexed.sort((a, b) => b.score - a.score);

  return indexed
    .slice(0, topN)
    .map(({ idx }) => productIds[idx])
    .filter((id): id is string => id != null);
};

/**
 * Resolve product-detection boxes from a parcel for overlay on the shelf image.
 *
 * Positional mapping: `query.product_detections[i]` -> `query.product_ids[i]`.
 * An unmatched detection (null/absent product_id) yields null and is
 * non-highlightable. An index is highlightable only when its product_id exists
 * in `activeProductIds` (the products of the currently shown planogram).
 */
export const resolveProductDetections = (
  parcel: OoSParcelSchema | undefined,
  activeProductIds: Set<string>,
): {
  boxes: { points: [number, number][] }[];
  productIdByIndex: (string | null)[];
  highlightableIndices: Set<number>;
} => {
  // product_detections/product_ids are typed on ImageContextSchema; only the
  // per-detection `points` is `unknown` (schema types it as an open object).
  const dets = parcel?.query?.product_detections;
  const pids = parcel?.query?.product_ids;
  if (!Array.isArray(dets) || dets.length === 0) {
    return { boxes: [], productIdByIndex: [], highlightableIndices: new Set() };
  }

  const boxes = dets.map((d) => ({
    points: ((d.points as number[][] | undefined) ?? []).map(
      (p) => [p[0], p[1]] as [number, number],
    ),
  }));
  const productIdByIndex = dets.map((_, i) => pids?.[i] ?? null);
  const highlightableIndices = new Set<number>();
  productIdByIndex.forEach((pid, i) => {
    if (pid && activeProductIds.has(pid)) highlightableIndices.add(i);
  });
  return { boxes, productIdByIndex, highlightableIndices };
};

export const getNextPendingTaskData = (
  parcel?: OoSParcelSchema,
  taskData?: any,
  task?: JobTaskDetailSchema,
) => {
  if (!parcel || !taskData) {
    return null;
  }

  const { query, references, store } = parcel;

  // Get the main query image ID
  const imageId = query.image_id;

  // Get the empty areas — from task data if originating from box draw, otherwise from parcel
  const emptyAreas = taskData.box_draw_task_id
    ? taskData.empty_areas || []
    : query.empty_area_detections?.map((detection) => ({
        id: (detection as any).id,
        empty_area_id: (detection as any).empty_area_id,
        points: detection.points,
        confidence: detection.confidence,
        class_index: detection.class_index,
      })) || [];

  // Derive the empty-area index and product index using the same fallback
  // chain that getNextPendingTaskData writes into the output's
  // query_empty_area_index field. This keeps the mapping key in sync with
  // what getReferenceCropPoints will later read from enriched.data.
  // Also fall back to the parcel-level query_to_product_index when
  // taskData.product_index is absent (mvpApiGetJobTask may omit it).
  const derivedQIdx =
    taskData.query_empty_area_index ?? taskData.empty_area_index ?? 0;
  const derivedProductIndex =
    taskData.product_index ?? parcel.query_to_product_index?.[derivedQIdx];

  // Get reference images based on reference_indices
  const referenceImages = references.map((reference, refIndex: number) => {
    return {
      image_id: reference.image_id,
      product_proposal_detections:
        reference.product_detections?.map((detection, index) => ({
          id: `detection-${refIndex}-${index}`,
          points: detection.points,
          confidence: detection.confidence,
          class_index: detection.class_index,
        })) || [],
      product_id: reference.product_ids?.[derivedProductIndex],
      product_ids_top_n: reference.product_ids_top_n?.[derivedProductIndex],
      product_dist_top_n: reference.product_dist_top_n?.[derivedProductIndex],
      empty_area_to_product_mapping: reference.empty_area_to_product,
      empty_area_to_detection_mapping:
        refIndex === 0 && derivedProductIndex != null
          ? { [derivedQIdx.toString()]: derivedProductIndex }
          : {},
    };
  });

  // Get product matches from the query
  // const productMatches = {
  //   item_ids: query.product_ids || [],
  //   // match_values: query.product_embeddings?.map(() => Math.random()) || [], // MOCKED: Random confidence values
  //   // proposals_indicies: query.product_detections?.map((_, index) => index) || [] // MOCKED: Using detection indices
  // };

  // Transform the task data to match the expected format
  return {
    ...task,
    image_id: imageId,
    data: {
      image_id: imageId,
      empty_areas: emptyAreas,
      // section_alt: null, // NOT IN NEW SCHEMA
      // section_main: "Unknown", // MOCKED: This would need to come from parcel metadata
      // source_job_id: taskData.source_job_id || null, // NOT IN NEW SCHEMA
      // section_choice: "main", // NOT IN NEW SCHEMA
      // original_job_id: null, // MOCKED: This would need to come from task metadata
      // product_matches: productMatches,
      empty_area_index:
        taskData.empty_area_index ?? taskData.query_empty_area_index ?? 0,
      query_empty_area_index:
        taskData.query_empty_area_index ?? taskData.empty_area_index ?? 0,
      // original_task_id: task.id, // NOT IN NEW SCHEMA
      reference_images: referenceImages,
      // alt_section_value: null, // NOT IN NEW SCHEMA
      internal_store_id: store?.id, // MOCKED: This would need to come from parcel metadata
      // prepared_image_name: `empty__1__${imageId}.jpg`, // MOCKED: Generated filename
      // section_alt_rankings: {}, // MOCKED: This would need to come from parcel metadata
      // thumbnail_image_name: `thumbnail__1__${imageId}.jpg`, // MOCKED: Generated filename
      reference_image_index: taskData.reference_indices?.[0] || 0,
      // section_main_distances: {}, // MOCKED: This would need to come from parcel metadata
      // production_planogram_id: "Unknown", // MOCKED: This would need to come from parcel metadata
      // planogram_localisation_result: { // MOCKED: This would need to come from parcel metadata
      //   confidence: 0.8,
      //   planogram_id: "Unknown",
      //   planogram_confidences: {},
      //   distractor_confidences: {}
      // },
      grouped_oos_events: taskData.grouped_oos_events || [],
      confirmation_result: taskData.confirmation_result || null,
      product_ids_top_n: query.product_ids_top_n,
      product_dist_top_n: query.product_dist_top_n,
      store: parcel?.store || null,
      identified_product_id: taskData.identified_product_id || null,
      planogram_id: taskData.planogram_id || null,
      box_draw_task_id: taskData.box_draw_task_id || null,
      // Box-draw fields needed for planogram resolution
      aisle_index: taskData.aisle_index ?? null,
      product_to_aisle: taskData.product_to_aisle ?? null,
      product_ids: taskData.product_ids ?? null,
      empty_areas_source: taskData.empty_areas_source ?? null,
    },
  };
};

/**
 * Reads back which grouped image set (see QuantitySlider) the task's result
 * was submitted against, so admin/QA review can default to the same set
 * instead of always falling back to the first one. The saved field lives at
 * different paths depending on how the task was fetched: `result` directly
 * on an admin-fetched confirmation task, or nested under
 * `data.confirmation_result` when the fetched task is a QA task wrapping the
 * original confirmation — the same dual-path convention used for other
 * answer fields (see EmptyAreaCard, ProductMatchCard, ProductSuggestions).
 */
export const getSavedGroupedEventsIndex = (
  task?: JobTaskDetailSchema | null,
): number | null => {
  const metadata =
    (task?.result as any)?.metadata ??
    (task?.data as any)?.confirmation_result?.metadata;
  const idx = metadata?.grouped_oos_events_index;
  return typeof idx === "number" ? idx : null;
};

/**
 * Derives the bounding-box crop points for the main (empty-area) image.
 * Shared between ItemVerification (rendering) and useNextTaskPrefetch (cache warming)
 * so both always use the same derivation and the prefetch warms the exact URL
 * the rendering component will request.
 */
export function getMainCropPoints(taskData: any): { points: any[] }[] | null {
  const idx = taskData?.empty_area_index ?? 0;
  const ea = taskData?.empty_areas?.[idx];
  return ea ? [{ points: ea.points ?? [] }] : null;
}

/**
 * Derives the bounding-box crop points for the reference image (product-proposal detection).
 * Shared between ItemVerification (rendering) and useNextTaskPrefetch (cache warming).
 */
export function getReferenceCropPoints(
  taskData: any,
): { points: any[] }[] | null {
  const refIdx = taskData?.reference_image_index ?? 0;
  const ref = taskData?.reference_images?.[refIdx];
  if (!ref) return null;
  const qIdx = taskData?.query_empty_area_index;
  const predictedIdx = ref.empty_area_to_detection_mapping?.[String(qIdx)];
  const points = ref.product_proposal_detections?.[predictedIdx]?.points ?? [];
  return [{ points }];
}

/**
 * Whether "question one" (the empty-area question) has an answer — either the
 * empty-area toggle is set to a boolean, or one of the alternative Q1 outcomes
 * was flagged. Single source of truth shared by OosConfirmationContent,
 * ItemVerification, and StickyActionsBar.
 */
export const hasQuestionOneAnswer = (
  emptyAreaConfirmed: boolean | null,
  resultMetadata: Record<string, any>,
): boolean =>
  typeof emptyAreaConfirmed === "boolean" ||
  !!resultMetadata.not_empty_area ||
  !!resultMetadata.not_main_aisle ||
  !!resultMetadata.unblurred_person_in_image ||
  !!resultMetadata.remaining_product_facings ||
  !!resultMetadata.blue_tag_present;

/**
 * Whether Q1 is answered as a clean "yes, this is an empty area" with no
 * rejection flags. Note: does NOT consider `blue_tag_present` — that's a
 * downstream constraint, not a Q1 rejection (a blue-tagged area is still a
 * real empty area), and the CV team treats `empty_area_confirmed`
 * independently of it.
 */
export const isValidEmptyArea = (
  emptyAreaConfirmed: boolean | null,
  resultMetadata: Record<string, any>,
): boolean =>
  !!emptyAreaConfirmed &&
  resultMetadata.not_empty_area === false &&
  resultMetadata.not_main_aisle === false &&
  resultMetadata.remaining_product_facings === false &&
  resultMetadata.unblurred_person_in_image === false;

/**
 * Whether Q1 was answered in a way that makes Q2 (item detection) moot —
 * either the empty-area check was invalidated by a rejection flag, or
 * blue_tag_present forces a no-match submission. Drives the
 * "No Product Location Required" placeholder in viewer mode.
 */
export const getShowNoLocationRequired = (
  emptyAreaConfirmed: boolean | null,
  resultMetadata: Record<string, any>,
): boolean =>
  (!isValidEmptyArea(emptyAreaConfirmed, resultMetadata) &&
    hasQuestionOneAnswer(emptyAreaConfirmed, resultMetadata)) ||
  resultMetadata.blue_tag_present === true;

/**
 * Whether the task should submit without a product match. Single source of
 * truth for the Submit-early gate (StickyActionsBar), the "No Product Match
 * Required" placeholder (ProductSuggestions), and the Q2 placeholder
 * derivation in ItemVerification.
 */
export const getNoMatchNeeded = (
  emptyAreaConfirmed: boolean | null,
  itemDetectionConfirmed: boolean | null,
  resultMetadata: Record<string, any>,
): boolean =>
  getShowNoLocationRequired(emptyAreaConfirmed, resultMetadata) ||
  itemDetectionConfirmed === false;

export function resolveGalleryImageUrl(
  image: ImageGallerySchema | undefined | null,
): string | undefined {
  if (!image) return undefined;
  return image.optimized_image ?? image.image;
}
